import { Router } from "@frui.ts/screens/dist";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { validate } from "@frui.ts/validation";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import * as Sentry from "@sentry/react";
import { Modules } from "config";
import AdHocReportRequest, { AdHocReportFile, AdHocReportRequestValidationRules } from "entities/adHocReportRequest";
import AdhocReportRequestResponse from "entities/adhocReportRequestResponse";
import { action, computed, observable } from "mobx";
import AdHocReportsRepository from "repositories/adHocReportsRepository";
import EventBus, { adhocReportFinish, adhocReportProcessed } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import { watchBusy } from "utils/busyWatcher";
import ListViewModel from "viewModels/listViewModel";
import { allowToDownload } from "./rights";
import { bound } from "@frui.ts/helpers";
import AdhocReportsService from "services/adhocReportsService";

const navigationName = "ad-hoc-reports";

@Router.registerRoute({ route: navigationName, name: Router.Self })
export default class AdHocReportsListViewModel extends ListViewModel<AdhocReportRequestResponse, ScreenBase, ScreenBase, any> {
  readonly modelName = "adhocReportRequestResponse";
  @observable items: AdhocReportRequestResponse[] = [];
  @observable fileResponse: AdHocReportFile;
  @observable request: AdHocReportRequest = new AdHocReportRequest();

  readonly idProperty = "id";

  constructor(
    protected repository: AdHocReportsRepository,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    private adhocReportsService: AdhocReportsService
  ) {
    super();
    this.navigationName = navigationName;
    attachAutomaticValidator(this.request, AdHocReportRequestValidationRules);
  }

  onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      this.eventBus.subscribe(adhocReportFinish, this.onReportFinished),
      this.eventBus.subscribe(adhocReportProcessed, this.onReportProcessed)
    );
    return super.onInitialize();
  }

  protected onActivate(): Promise<void> {
    this.loadData();
    return super.onActivate();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `adHocReports.${code}`, size);
  }

  @action.bound
  @watchBusy
  async onFormSubmit(forceRequest = false) {
    if (!validate(this.request)) {
      return;
    }
    this.request.forceRequest = forceRequest;
    await this.onGenerateReportClick(this.request);
  }

  @action.bound
  @watchBusy
  async onGenerateReportClick(request: AdHocReportRequest) {
    try {
      const response = await this.repository.generateNewReport(request);

      if (response.existingReportId) {
        this.eventBus.publish(adhocReportFinish({ adhocReport: response }));
      }
      if (!response.existingReportId && response.createdAdHocReportRequest?.id) {
        this.eventBus.publish(adhocReportProcessed({ adhocReport: response }));
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @action.bound
  async onReportFinished(event: ReturnType<typeof adhocReportFinish>) {
    const { confirmed, generate } = await this.adhocReportsService.showFinishedConfirmation();
    const report = this.items.find(x => x.id === event.payload.adhocReport.existingReportId);
    if ((!confirmed && !generate) || !event.payload.adhocReport.existingReportId) {
      return;
    }

    if (confirmed && !generate && report) {
      try {
        await this.download(report);
        this.notificationService.add({ title: this.translate("exported"), type: NotificationType.SUCCESS });
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    if (confirmed && generate) {
      try {
        if (report) {
          await this.onFormSubmit(true);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }

  @action.bound
  async onReportProcessed(event: ReturnType<typeof adhocReportProcessed>) {
    const confirmed = await this.adhocReportsService.showProcessedConfirmation();
    if (event.payload.adhocReport.createdAdHocReportRequest) {
      this.items = [event.payload.adhocReport.createdAdHocReportRequest, ...this.items];
    }
    if (confirmed) {
      return;
    }
  }

  @action.bound
  @watchBusy
  async onDownload(item: AdhocReportRequestResponse) {
    await this.download(item);
  }

  @bound
  downloadFile(fileName: string, fileUrl: string) {
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  @computed
  get canMakeReport() {
    return this.securityService.allowTo("charges.reports", "write");
  }

  async download(item: AdhocReportRequestResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        this.downloadFile(this.fileResponse.downloadFileName, this.fileResponse.fileUrl);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
